import { WsBaseInterface } from './WsBaseInterface';

import { CallbacksManager } from '~spa/Utils/CallbacksManager';
import webSocketSubscriptionStore from '../utils/WsSubscriptionStore';
import wsSbLiveDataSubscriptionStores from '../utils/WsSbLiveDataSubscriptionStore';

import { getRegulationTypeId, getLanguageCode } from '~shared/utils/user';

const {
    subscribe: subscribeToStore,
    unsubscribe: unsubscribeFromStore,
    getRegistrationPayload,
    clearSubscriptionStores,
} = webSocketSubscriptionStore;

class WsInterface extends WsBaseInterface {
    #msgCallbacks = new Map();
    #killCallbacks = new CallbacksManager();

    constructor(config) {
        super(config);
        this.addOnMessageCallback(
            (msg) => this.messageReceivedHandler(msg)
        );
    }

    messageReceivedHandler(message) {
        const { action } = message;

        this.#msgCallbacks.get(action)?.runAllCallbacks(message);
    }

    onMessage(action, callback) {
        if (!this.#msgCallbacks.has(action)) {
            this.#msgCallbacks.set(action, new CallbacksManager());
        }

        return this.#msgCallbacks.get(action).addCallback(callback);
    }

    onConnection(callback) {
        this.addOnOpenCallback(callback);
    }

    onConnectionClose(callback) {
        this.addOnCloseCallback(callback);
    }

    onKill(callback) {
        this.#killCallbacks.addCallback(callback);
    }

    kill() {
        this.#msgCallbacks.clear();
        this.#killCallbacks.runAllCallbacks();
        this.offConnectionCallbacks();
        this.disconnect();
    }
}

export class GeneralWsInterface extends WsInterface {
    constructor(config) {
        super(config);

        this.onConnection(() => {
            const payload = getRegistrationPayload();
            if (payload.length) {
                this.register(payload);
            }
        });

        this.onKill(clearSubscriptionStores);
    }

    subscribe(subscriptionData) {
        const { registerPayload } = subscribeToStore(
            subscriptionData
        );

        if (registerPayload.length) {
            this.register(registerPayload);
        }
    }

    unsubscribe(unsubscribeData) {
        const { registerPayload, unregisterPayload } = unsubscribeFromStore(
            unsubscribeData
        );

        if (unregisterPayload.length) {
            this.unregister(unregisterPayload);
        }

        if (registerPayload.length) {
            this.register(registerPayload);
        }
    }

    register(data) {
        const payload = {
            action: 'register',
            data,
            lang: getLanguageCode(),
            regulation_type_id: getRegulationTypeId(),
        };

        this.send(payload);
    }

    unregister(data) {
        this.send({
            action: 'unregister',
            data,
        });
    }
}
/**
 * Class to handle the sportsbook_live_data WebSocket connection
 * Important - this connection is created/destroyed on login/logout. If that changes then there is
 * subscribe/unsubscribe logic in BetslipContainer that needs changing
 * @todo refactor: https://dedsert.atlassian.net/browse/AM-7627
 */
export class SbLiveDataInterface extends WsInterface {
    #subscriptionStores = {};

    constructor(config) {
        super(config);

        this.#subscriptionStores = wsSbLiveDataSubscriptionStores;

        this.onConnection(
            () => {
                Object.keys(this.#subscriptionStores).forEach(
                    storeType => {
                        const wsPayload = this.#subscriptionStores[storeType].getReconnectionPayload();

                        if (wsPayload !== false) {
                            this.subscribe(wsPayload);
                        }
                    }
                );
            },
        );

        this.onKill(
            () => {
                Object.keys(this.#subscriptionStores).forEach(
                    storeType => {
                        this.#subscriptionStores[storeType].clearSubscriptions();
                    }
                );
            },
        );
    }

    /**
     * Replace existing subscriptions with new ones
     * For SOME types it is not possible to have > 1 component "interested" in a particular
     * subscription, e.g. gtp_betbuilder type can only ever appear in the Betslip. In this case the
     * "replace" strategy works. Do not use for types that have expensive (un)subscribe calls.
     * @param {Object} payload subscription payload
     * @param {string} payload.type subscription type, e.g. gtp_betbuilder
     * @param {Array<Array<number>>} [payload.combinations=[]] for gtp_betbuilder type, Array of
     * array of combination selection ids e.g. [ [1, 2, 3 ], [9, 8, 7] ]
     * @returns {void}
     */
    cleanSubscribe(payload) {
        this.unsubscribe({ type: payload.type, combinations: null });
        this.subscribe(payload);
    }

    /**
     * Subscribe for updates
     * @param {Object} payload subscription payload
     * @param {string} payload.type subscription type, e.g. gtp_betbuilder
     * @param {Array<Array<number>>} [payload.combinations=[]] FOR gtp_betbuilder type, Array of
     * array of combination selection ids e.g. [ [1, 2, 3 ], [9, 8, 7] ]. Note that other types can
     * use different payload properties (e.g. payload.data, payload.myProperty) - it is up to the SLD service to
     * consume these
     * @param {boolean} [onlyAddToStore=false] only add to store without sending register message.
     * This logic specifically to support creating/destroying the WS connection on login/logout.
     * @returns {void}
     */
    subscribe(payload, onlyAddToStore = false) {
        const { type } = payload;
        if (!type) {

            return;
        }

        const wsMessage = this.#subscriptionStores[type].subscribe(payload, onlyAddToStore);
        if (wsMessage) {

            this.send(wsMessage);
        }
    }

    /**
     * Unsubscribe from updates.
     * If param is empty, unsubscribe from everything and set this.#hasActiveSubscriptions to false
     * @param {Object} payload subscription payload
     * @param {string} payload.type subscription type, e.g. gtp_betbuilder
     * @param {Array<Array<number>>} [payload.combinations=[]] for gtp_betbuilder type, Array of
     * array of combination selection ids e.g. [ [1, 2, 3 ], [9, 8, 7] ]
     * @returns {void}
     */
    unsubscribe(payload = {}) {
        const { type } = payload;
        if (!type) {

            return;
        }

        const wsMessage = this.#subscriptionStores[type].unsubscribe(payload);
        if (wsMessage) {

            this.send(wsMessage);
        }
    }
}

export class CashoutWsInterface extends WsInterface {}
